import { Stack, PrimaryButton, DefaultButton, ProgressIndicator } from '@fluentui/react';
import React, { useState } from 'react';
import SectionTitle from './common/SectionTitle';
import BasicChecks from './sections/BasicChecks';
import AdditionalInfo from './sections/AdditionalInfo';
import ScreenshotsUpload from './sections/ScreenshotsUpload';
import FirewallSettings from './sections/FirewallSettings';
import { generatePDFReport } from './utils/generatePDF';
import { ChecklistData } from './types';
import IvicosStrings from 'kits/language/stringKit';
import CallIssuesAndNetwork from './sections/CallIssuesAndNetwork';

const stackTokens = { childrenGap: 25, padding: 20 };

const sections = [
    { id: 'basic', title: IvicosStrings.sectionBasic, component: BasicChecks },
    { id: 'call-network', title: IvicosStrings.sectionCallNetwork, component: CallIssuesAndNetwork },
    { id: 'additional', title: IvicosStrings.sectionAdditional, component: AdditionalInfo },
    { id: 'firewall', title: IvicosStrings.sectionFirewall, component: FirewallSettings },
    { id: 'screenshots', title: 'Screenshots', component: ScreenshotsUpload }
];

const WebRTCDiagnostics: React.FC = () => {
    const userLanguage = localStorage.getItem('UserLanguageChoice') || 'de';
    const [currentStep, setCurrentStep] = useState(0);
    const [checklistData, setChecklistData] = useState<ChecklistData>({
        browser: '',
        browserUpdated: '',
        browserVersion: '',
        internetConnection: '',
        microphoneWorking: '',
        cameraWorking: '',
        permissionsGranted: '',
        callIssues: '',
        othersCanSeeHear: '',
        usingVPN: '',
        managedIT: '',
        regionalIT: '',
        operatingSystem: '',
        screenshots: [],
        firewallConfig: {
            turnServer: false,
            jvbServer: false,
            jitsiServer: false,
            port80: false,
            port443: false,
            port4443: false,
            port10000: false
        }
    });

    const hasEnteredData = () => {
        // Check text fields
        const textFields = [
            checklistData.browser,
            checklistData.browserUpdated,
            checklistData.browserVersion,
            checklistData.internetConnection,
            checklistData.microphoneWorking,
            checklistData.cameraWorking,
            checklistData.permissionsGranted,
            checklistData.callIssues,
            checklistData.othersCanSeeHear,
            checklistData.usingVPN,
            checklistData.managedIT,
            checklistData.operatingSystem
        ];

        // Check if any text field has data
        const hasTextData = textFields.some((field) => field && field.trim() !== '');

        // Check if any firewall settings are true
        const hasFirewallData = Object.values(checklistData.firewallConfig).some((value) => value === true);

        // Check if any screenshots are uploaded
        const hasScreenshots = checklistData.screenshots.length > 0;

        return hasTextData || hasFirewallData || hasScreenshots;
    };

    const handleNext = () => {
        if (currentStep < sections.length - 1) {
            setCurrentStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const CurrentSection = sections[currentStep].component;

    return (
        <Stack tokens={stackTokens} style={{ maxWidth: '80vw', margin: '0 auto 20px auto' }}>
            <SectionTitle>{IvicosStrings.webRTCDiagnosticsChecklistTitle}</SectionTitle>

            <ProgressIndicator
                percentComplete={(currentStep + 1) / sections.length}
                label={`${currentStep + 1} ${userLanguage === 'de' ? 'von' : 'of'} ${sections.length} ${userLanguage === 'de' ? 'Schritte' : 'Steps'}`}
                description={sections[currentStep].title}
            />

            <CurrentSection checklistData={checklistData} setChecklistData={setChecklistData} />

            <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="end">
                {currentStep > 0 && <DefaultButton text={IvicosStrings.back} onClick={handleBack} styles={{ root: { minWidth: 100 } }} />}
                {currentStep < sections.length - 1 ? (
                    <PrimaryButton text={IvicosStrings.nextActionTitle2} onClick={handleNext} styles={{ root: { minWidth: 100 } }} />
                ) : (
                    <PrimaryButton
                        text={IvicosStrings.webRTCDiagnosticsGenerateReportButton}
                        onClick={() => generatePDFReport(checklistData)}
                        disabled={!hasEnteredData()}
                        styles={{ root: { minWidth: 100 } }}
                    />
                )}
            </Stack>
        </Stack>
    );
};

export default WebRTCDiagnostics;
